import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Briefcase,
  Building2,
  Calculator,
  Check,
  CircleDollarSign,
  ExternalLink,
  FileText,
  Landmark,
  PhoneCall,
  RotateCw,
  X,
} from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { orgScopedPath } from "@/lib/orgDoor";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import Loader from "@/components/ui/loader";
import { EmptyPanel, PageHero, PagePanel, PageShell, PanelHeading } from "@/components/interior-page";
import {
  STATUS_HINT,
  STATUS_STYLE,
  fmtDate,
  loadFundingPartners,
  statusLabel,
  type FundingApplication,
  type FundingPartner,
} from "@/lib/funding";
import { cn } from "@/lib/utils";

/* The Funding Marketplace landing page: one card per funding company.
   Duneland Financial is the first; the grid fills in on its own as companies
   are added to app/services/funding_partners.py — nothing here is per-company.

   Each card carries the buyer's own submissions to that lender, so "where does
   my application stand" is answered on the same screen they applied from.

   ?listing=<id> carries the deal being financed (from a listing page or the
   SBA calculator) through to the apply form. */

function StatusChip({ status }: { status: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold",
        STATUS_STYLE[status] ?? STATUS_STYLE.submitted,
      )}
    >
      {statusLabel(status)}
    </span>
  );
}

/** The buyer's own applications, with what each status means for them. */
function ApplicationList({ applications }: { applications: FundingApplication[] }) {
  return (
    <ul className="mt-2.5 space-y-3">
      {applications.map((a) => (
        <li key={a.id}>
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
            <span className="min-w-0">
              <span className="font-mono text-sm font-bold text-foreground">{a.reference_code}</span>
              <span className="ml-2 text-xs text-muted-foreground">
                Sent {fmtDate(a.created_at)}
                {a.status_changed_at && ` · Updated ${fmtDate(a.status_changed_at)}`}
              </span>
            </span>
            <StatusChip status={a.status} />
          </div>
          {a.listing_title && (
            <p className="mt-1 flex items-center gap-1.5 text-xs text-foreground/80">
              <Briefcase className="h-3 w-3 shrink-0 text-primary" />
              <span className="truncate">{a.listing_title}</span>
            </p>
          )}
          {STATUS_HINT[a.status] && <p className="mt-1 text-xs text-muted-foreground">{STATUS_HINT[a.status]}</p>}
        </li>
      ))}
    </ul>
  );
}

function LenderCard({
  partner,
  applications,
  applyHref,
}: {
  partner: FundingPartner;
  applications: FundingApplication[];
  applyHref: string;
}) {
  const applied = applications.length > 0;

  return (
    <article className="flex h-full flex-col rounded-2xl border border-border/75 bg-card p-6 shadow-card transition-colors duration-200 hover:border-primary/35">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-primary/12 text-primary">
          {partner.logo_url ? (
            <img src={partner.logo_url} alt="" className="h-full w-full object-contain p-1.5" />
          ) : (
            <Landmark className="h-6 w-6" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-2xl font-bold leading-tight text-foreground">{partner.name}</h3>
          <p className="mt-0.5 text-sm font-medium text-muted-foreground">{partner.tagline}</p>
        </div>
        {partner.website && (
          // Text colour goes on the span: the unlayered `a { color: inherit }`
          // reset discards text-* utilities on the anchor itself.
          <a href={partner.website} target="_blank" rel="noopener noreferrer" className="shrink-0">
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
              Website <ExternalLink className="h-3 w-3" />
            </span>
          </a>
        )}
      </div>

      {partner.tags.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {partner.tags.map((t) => (
            <span
              key={t}
              className="rounded-md border border-primary/20 bg-primary/5 px-2 py-0.5 text-[0.68rem] font-semibold text-foreground/80"
            >
              {t}
            </span>
          ))}
        </div>
      )}

      {partner.facts.length > 0 && (
        <dl className="mt-4 grid grid-cols-2 gap-3 rounded-xl border border-border/60 bg-muted/15 p-3 sm:grid-cols-3">
          {partner.facts.map((f) => (
            <div key={f.label} className="min-w-0">
              <dt className="text-[0.6rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">{f.label}</dt>
              <dd className="mt-0.5 text-sm font-bold text-foreground">{f.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {partner.blurb && <p className="mt-4 text-sm leading-6 text-muted-foreground">{partner.blurb}</p>}

      {partner.highlights.length > 0 && (
        <ul className="mt-4 space-y-2">
          {partner.highlights.map((h) => (
            <li key={h} className="flex items-start gap-2 text-sm leading-6 text-foreground/85">
              <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" />
              {h}
            </li>
          ))}
        </ul>
      )}

      {/* The buyer's own applications to this lender. Sits above the button so
          "I already applied" is read before the invitation to apply again. */}
      {applied && (
        <div className="mt-5 rounded-xl border border-border/60 bg-muted/20 p-4">
          <p className="text-[0.62rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
            Your {applications.length === 1 ? "application" : "applications"}
          </p>
          <ApplicationList applications={applications} />
        </div>
      )}

      <div className="mt-auto pt-6">
        <Link to={applyHref} className="block">
          <Button type="button" size="lg" className="w-full gap-2">
            <FileText className="h-4 w-4" />
            {applied ? "Submit another inquiry" : "Submit inquiry"}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>
    </article>
  );
}

const STEPS = [
  {
    icon: Calculator,
    title: "Size the loan",
    body: "Run the deal through the SBA calculator to see the loan, your cash injection and DSCR.",
  },
  {
    icon: FileText,
    title: "Send one inquiry per lender",
    body: "A short pre-qualification form. Your answers go only to the lender you choose.",
  },
  {
    icon: PhoneCall,
    title: "The lender contacts you",
    body: "They review your packet and reach out directly. Track each inquiry's status here.",
  },
];

export default function FundingMarketplacePage() {
  const { user } = useAuth();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const listingId = searchParams.get("listing");

  const [partners, setPartners] = useState<FundingPartner[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [applications, setApplications] = useState<FundingApplication[]>([]);
  const [listingTitle, setListingTitle] = useState<string | null>(null);

  const loadPartners = useCallback(() => {
    setLoadFailed(false);
    setPartners(null);
    loadFundingPartners()
      .then(setPartners)
      .catch(() => setLoadFailed(true));
  }, []);

  useEffect(() => {
    loadPartners();
    // The buyer's own submissions, grouped onto the cards below. A failure here
    // must not blank the lender list — the cards are still useful without it.
    api
      .get<FundingApplication[]>("/funding/applications")
      .then(setApplications)
      .catch(() => toast.error("Could not load the status of your applications."));
  }, [loadPartners, toast]);

  // The deal being financed, named in the banner. A bad id just drops the banner.
  useEffect(() => {
    if (!listingId) {
      setListingTitle(null);
      return;
    }
    let stale = false;
    api
      .get<{ title: string }>(`/business-listings/${listingId}`)
      .then((l) => !stale && setListingTitle(l.title))
      .catch(() => !stale && setListingTitle(null));
    return () => {
      stale = true;
    };
  }, [listingId]);

  const activeSlugs = useMemo(() => new Set((partners ?? []).map((p) => p.slug)), [partners]);

  const byPartner = useMemo(() => {
    const map = new Map<string, FundingApplication[]>();
    for (const a of applications) {
      const list = map.get(a.partner_slug) ?? [];
      list.push(a);
      map.set(a.partner_slug, list);
    }
    return map;
  }, [applications]);

  // Applications to a lender no longer on the platform. Without this they
  // would vanish along with the lender's card.
  const pastApplications = useMemo(
    () => (partners ? applications.filter((a) => !activeSlugs.has(a.partner_slug)) : []),
    [applications, activeSlugs, partners],
  );

  const applyHref = (slug: string) =>
    orgScopedPath(`/buyer/funding/${slug}`, user?.org_slug) + (listingId ? `?listing=${encodeURIComponent(listingId)}` : "");

  const clearListing = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("listing");
    setSearchParams(next, { replace: true });
  };

  return (
    <PageShell>
      <PageHero
        eyebrow="Funding Marketplace"
        title="Acquisition"
        titleAccent="financing"
        description="Pre-qualify with lenders who finance business acquisitions. Pick a lender, send a short inquiry, and they contact you directly. Your answers go only to the lender you choose."
        icon={CircleDollarSign}
      />

      {listingId && listingTitle && (
        <div className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-primary/30 bg-primary/8 px-5 py-3.5">
          <Briefcase className="h-4 w-4 shrink-0 text-primary" />
          <p className="min-w-0 flex-1 text-sm text-foreground">
            Financing for <strong className="font-semibold">{listingTitle}</strong>. Your inquiry will include this
            deal.
          </p>
          <button
            type="button"
            onClick={clearListing}
            className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" /> Not for this deal
          </button>
        </div>
      )}

      <PagePanel className="mb-5">
        <PanelHeading title="How it works" />
        <ol className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <li key={s.title} className="flex gap-3 rounded-xl border border-border/60 bg-muted/15 p-4">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/12 text-primary">
                <s.icon className="h-4.5 w-4.5" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-foreground">
                  {i + 1}. {s.title}
                </p>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">{s.body}</p>
                {i === 0 && (
                  <Link to={orgScopedPath("/buyer/sba-calculator", user?.org_slug)} className="mt-1.5 inline-block">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                      Open SBA calculator <ArrowRight className="h-3 w-3" />
                    </span>
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ol>
      </PagePanel>

      <PagePanel>
        <PanelHeading
          title="Our funding partners"
          description={
            partners ? `${partners.length} lender${partners.length === 1 ? "" : "s"} available.` : undefined
          }
        />

        {loadFailed ? (
          <div className="rounded-2xl border border-dashed border-border/80 bg-muted/20 px-6 py-10 text-center">
            <AlertTriangle className="mx-auto h-8 w-8 text-muted-foreground/60" />
            <p className="mt-3 font-bold text-foreground">Could not load the lenders</p>
            <p className="mt-1 text-sm text-muted-foreground">Check your connection and try again.</p>
            <Button type="button" variant="outline" className="mt-4 gap-2" onClick={loadPartners}>
              <RotateCw className="h-4 w-4" /> Retry
            </Button>
          </div>
        ) : partners === null ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : partners.length === 0 ? (
          <EmptyPanel
            icon={Building2}
            title="No funding partners yet"
            description="Lenders will appear here as they come onto the platform."
          />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              {partners.map((p) => (
                <LenderCard
                  key={p.slug}
                  partner={p}
                  applications={byPartner.get(p.slug) ?? []}
                  applyHref={applyHref(p.slug)}
                />
              ))}
            </div>
            {/* A slim strip, not a grid tile: a card-sized placeholder beside one
                real lender read as a second lender at a glance. */}
            <div className="mt-5 flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-border/70 bg-muted/15 px-4 py-3">
              <Landmark className="h-4 w-4 shrink-0 text-muted-foreground" />
              <p className="min-w-0 flex-1 text-sm text-muted-foreground">
                More lenders are being added. Each takes its own inquiry, so you can pre-qualify with several.
              </p>
              <Link to={orgScopedPath("/buyer/support", user?.org_slug)}>
                <span className="text-sm font-semibold text-primary hover:underline">Suggest a lender</span>
              </Link>
            </div>
          </>
        )}
      </PagePanel>

      {pastApplications.length > 0 && (
        <PagePanel className="mt-5">
          <PanelHeading
            title="Earlier applications"
            description="Inquiries to lenders that are no longer listed. The lender still has your packet."
          />
          <div className="space-y-4">
            {[...new Set(pastApplications.map((a) => a.partner_slug))].map((slug) => {
              const apps = pastApplications.filter((a) => a.partner_slug === slug);
              return (
                <div key={slug} className="rounded-xl border border-border/60 bg-muted/20 p-4">
                  <p className="text-sm font-bold text-foreground">{apps[0].partner_name}</p>
                  <ApplicationList applications={apps} />
                </div>
              );
            })}
          </div>
        </PagePanel>
      )}
    </PageShell>
  );
}
